import React, { useEffect, useEffectEvent, useId, useRef, useState } from 'react';
import { createPortal } from 'react-dom';

const BOX_WIDTH = 374; // 35rem + extra padding (1.2rem * 2) + border
const BOX_HEIGHT_FALLBACK = 120;
const EDGE_MARGIN = 20;
const ARROW_BOUNDS = 10;
const BOX_CORNER_RADIUS = 10;
const ARROW_SAFE_PADDING = 6;
const PORTAL_GAP = 10;
const OPEN_DELAY_MS = 150;
const CLOSE_DELAY_MS = 225;

const getSafeArrowInset = (boxWidth: number) => {
    const preferredInset = Math.max(ARROW_BOUNDS, BOX_CORNER_RADIUS + ARROW_SAFE_PADDING);
    return Math.min(preferredInset, Math.max(boxWidth / 2, ARROW_BOUNDS));
};

type Position = 'top' | 'bottom' | 'left' | 'right';

interface InfoBoxProps {
    children: React.ReactNode;
    className?: string;
    portalClassName?: string;
    iconClassName?: string;
    triggerAriaLabel?: string;
    windowContext?: Window | null;
}

const InfoBox: React.FC<InfoBoxProps> = ({
    children,
    className = '',
    portalClassName = '',
    iconClassName = 'material-icons',
    triggerAriaLabel = 'Vis forklaring av vegsystemreferanse',
    windowContext,
}) => {
    const [isVisible, setIsVisible] = useState(false);
    const [boxPosition, setBoxPosition] = useState({ top: 0, left: 0 });
    const [calculatedPosition, setCalculatedPosition] = useState<Position>('top');
    const [boxMaxHeight, setBoxMaxHeight] = useState<number | null>(null);
    const [isScrollable, setIsScrollable] = useState(false);
    const [announceText, setAnnounceText] = useState('');
    const tooltipId = useId();

    const containerRef = useRef<HTMLDivElement>(null);
    const iconRef = useRef<HTMLElement>(null);
    const boxRef = useRef<HTMLDivElement>(null);
    const openTimeout = useRef<NodeJS.Timeout | null>(null);
    const closeTimeout = useRef<NodeJS.Timeout | null>(null);

    const clearOpenTimeout = () => {
        if (openTimeout.current) {
            clearTimeout(openTimeout.current);
            openTimeout.current = null;
        }
    };

    const clearCloseTimeout = () => {
        if (closeTimeout.current) {
            clearTimeout(closeTimeout.current);
            closeTimeout.current = null;
        }
    };

    const scheduleOpen = () => {
        clearCloseTimeout();
        clearOpenTimeout();
        openTimeout.current = setTimeout(() => {
            setIsVisible(true);
            openTimeout.current = null;
        }, OPEN_DELAY_MS);
    };

    const scheduleClose = () => {
        clearOpenTimeout();
        clearCloseTimeout();
        closeTimeout.current = setTimeout(() => {
            setIsVisible(false);
            closeTimeout.current = null;
        }, CLOSE_DELAY_MS);
    };

    const updatePosition = useEffectEvent(() => {
        if (!iconRef.current) return;

        const win = windowContext || window;
        const doc = win.document;
        const iconRect = iconRef.current.getBoundingClientRect();
        const scrollTop = win.pageYOffset || doc.documentElement.scrollTop;
        const scrollLeft = win.pageXOffset || doc.documentElement.scrollLeft;
        const viewportHeight = win.innerHeight - EDGE_MARGIN * 2;
        const viewportRight = scrollLeft + win.innerWidth - EDGE_MARGIN;
        const viewportBottom = scrollTop + win.innerHeight - EDGE_MARGIN;

        const boxWidth = boxRef.current?.offsetWidth || BOX_WIDTH;
        const boxScrollHeight = boxRef.current?.scrollHeight || BOX_HEIGHT_FALLBACK;
        const boxHeight = boxRef.current?.offsetHeight || BOX_HEIGHT_FALLBACK;
        const isTooTallForViewport = boxScrollHeight > viewportHeight;
        const effectiveBoxHeight = isTooTallForViewport ? viewportHeight : boxHeight;
        const iconCenterX = iconRect.left + scrollLeft + iconRect.width / 2;
        const iconCenterY = iconRect.top + scrollTop + iconRect.height / 2;
        const centeredLeft = iconCenterX - boxWidth / 2;
        const safeArrowInset = getSafeArrowInset(boxWidth);
        const minViewportLeft = scrollLeft + EDGE_MARGIN;
        const maxViewportLeft = viewportRight - boxWidth;
        const minArrowAlignedLeft = iconCenterX - (boxWidth - safeArrowInset);
        const maxArrowAlignedLeft = iconCenterX - safeArrowInset;
        const minTopBottomLeft = Math.max(minViewportLeft, minArrowAlignedLeft);
        const maxTopBottomLeft = Math.min(maxViewportLeft, maxArrowAlignedLeft);
        const canPlaceTopOrBottom = minTopBottomLeft <= maxTopBottomLeft;

        const spaceLeft = iconRect.left - EDGE_MARGIN;
        const spaceRight = win.innerWidth - iconRect.right - EDGE_MARGIN;
        const canPlaceLeft = spaceLeft >= boxWidth + PORTAL_GAP;
        const canPlaceRight = spaceRight >= boxWidth + PORTAL_GAP;

        const spaceAbove = iconRect.top;
        const spaceBelow = win.innerHeight - iconRect.bottom;
        const verticalPosition: Position =
            spaceAbove < effectiveBoxHeight && spaceBelow >= effectiveBoxHeight
                ? 'bottom'
                : 'top';

        let position: Position = verticalPosition;
        const preferredSide: Position =
            canPlaceRight && (!canPlaceLeft || spaceRight >= spaceLeft)
                ? 'right'
                : 'left';

        if (isTooTallForViewport) {
            position =
                canPlaceLeft || canPlaceRight
                    ? preferredSide
                    : spaceRight >= spaceLeft
                      ? 'right'
                      : 'left';
        } else if (!canPlaceTopOrBottom && (canPlaceLeft || canPlaceRight)) {
            position = preferredSide;
        }

        setCalculatedPosition(position);
        setIsScrollable(isTooTallForViewport);
        setBoxMaxHeight(isTooTallForViewport ? viewportHeight : null);

        let top =
            position === 'top' ? iconRect.top + scrollTop : iconRect.bottom + scrollTop;
        let left = canPlaceTopOrBottom
            ? Math.max(minTopBottomLeft, Math.min(centeredLeft, maxTopBottomLeft))
            : Math.max(minViewportLeft, Math.min(centeredLeft, maxViewportLeft));

        if (position === 'left' || position === 'right') {
            top = Math.max(
                scrollTop + EDGE_MARGIN,
                Math.min(
                    iconCenterY - effectiveBoxHeight / 2,
                    viewportBottom - effectiveBoxHeight
                )
            );
            const desiredLeft =
                position === 'right'
                    ? iconRect.right + scrollLeft + PORTAL_GAP
                    : iconRect.left + scrollLeft - boxWidth - PORTAL_GAP;
            left = Math.max(
                scrollLeft + EDGE_MARGIN,
                Math.min(desiredLeft, viewportRight - boxWidth)
            );
        }

        setBoxPosition({ top, left });
    });

    useEffect(() => {
        if (!isVisible) return;

        updatePosition();

        const win = windowContext || window;
        const handlePositionUpdate = () => updatePosition();

        win.addEventListener('resize', handlePositionUpdate);
        win.addEventListener('scroll', handlePositionUpdate);

        return () => {
            win.removeEventListener('resize', handlePositionUpdate);
            win.removeEventListener('scroll', handlePositionUpdate);
        };
    }, [isVisible, windowContext]);

    // Extract and format text for screen readers
    // Walks the DOM tree to create a readable text version with appropriate punctuation and pauses
    const getAccessibleText = (element: HTMLElement): string => {
        if (!element) return '';

        const formatNode = (node: Node): string => {
            if (node.nodeType === Node.TEXT_NODE) {
                return node.textContent?.trim() || '';
            }

            if (node.nodeType === Node.ELEMENT_NODE) {
                const element = node as HTMLElement;
                const tagName = element.tagName.toLowerCase();

                const childTexts = Array.from(node.childNodes)
                    .map(formatNode)
                    .filter(text => text.length > 0);

                switch (tagName) {
                    case 'a':
                        return childTexts.join(' ') + ' (lenke)';
                    case 'p':
                    case 'div':
                    case 'li':
                        return childTexts.join(' ') + '. ';
                    case 'br':
                        return '. ';
                    case 'strong':
                    case 'b':
                    case 'em':
                    case 'i':
                        return childTexts.join(' ');
                    case 'span':
                        const text = childTexts.join(' ');
                        return text + (text.match(/^[A-Z]+:/) ? ' ' : '. ');
                    default:
                        return childTexts.join(' ') + ' ';
                }
            }

            return '';
        };

        // Normalize whitespace and remove duplicate periods
        return formatNode(element)
            .replace(/\s+/g, ' ')
            .replace(/\.\s*\./g, '.')
            .trim();
    };

    useEffect(() => {
        if (isVisible) {
            updatePosition();
            // Set text content for screen reader
            if (boxRef.current) {
                const accessibleText = getAccessibleText(boxRef.current);
                setAnnounceText(accessibleText);
            }
        } else {
            setAnnounceText('');
        }
    }, [calculatedPosition, isVisible]);

    useEffect(() => {
        if (!isVisible) return;
        const doc = windowContext?.document ?? document;
        const handleGlobalKeyDown = (e: KeyboardEvent) => {
            if (e.key === 'Escape') {
                setIsVisible(false);
            }
        };

        doc.addEventListener('keydown', handleGlobalKeyDown);
        return () => {
            doc.removeEventListener('keydown', handleGlobalKeyDown);
        };
    }, [isVisible, windowContext]);

    useEffect(() => {
        return () => {
            clearOpenTimeout();
            clearCloseTimeout();
        };
    }, []);

    const handleMouseEnter = () => {
        scheduleOpen();
    };

    const handleMouseLeave = () => {
        scheduleClose();
    };

    const handleFocus = () => {
        scheduleOpen();
    };

    const handleBlur = () => scheduleClose();

    const handleKeyDown = (e: React.KeyboardEvent<HTMLDivElement>) => {
        if (e.key === 'Escape') {
            setIsVisible(false);
        }
    };

    const getArrowStyles = () => {
        if (!iconRef.current) return {};

        const win = windowContext || window;
        const doc = win.document;
        const iconRect = iconRef.current.getBoundingClientRect();
        const scrollTop = win.pageYOffset || doc.documentElement.scrollTop;
        const scrollLeft = win.pageXOffset || doc.documentElement.scrollLeft;
        const boxWidth = boxRef.current?.offsetWidth || BOX_WIDTH;
        const boxHeight = boxRef.current?.offsetHeight || BOX_HEIGHT_FALLBACK;
        const iconCenterX = iconRect.left + scrollLeft + iconRect.width / 2;
        const iconCenterY = iconRect.top + scrollTop + iconRect.height / 2;
        const safeArrowInset = getSafeArrowInset(boxWidth);

        const arrowPosition = iconCenterX - boxPosition.left;
        const clampedArrowPos = Math.max(
            safeArrowInset,
            Math.min(boxWidth - safeArrowInset, arrowPosition)
        );
        const verticalArrowPosition = iconCenterY - boxPosition.top;
        const clampedVerticalArrowPos = Math.max(
            ARROW_BOUNDS,
            Math.min(boxHeight - ARROW_BOUNDS, verticalArrowPosition)
        );

        const baseArrowStyles = {
            content: '""',
            position: 'absolute' as const,
            borderWidth: '0.5rem',
            borderStyle: 'solid' as const,
        };

        switch (calculatedPosition) {
            case 'top':
                return {
                    ...baseArrowStyles,
                    left: `${clampedArrowPos}px`,
                    marginLeft: '-0.6rem',
                    top: '100%',
                    borderColor: '#dadada transparent transparent transparent',
                };
            case 'bottom':
                return {
                    ...baseArrowStyles,
                    left: `${clampedArrowPos}px`,
                    marginLeft: '-0.6rem',
                    bottom: '100%',
                    borderColor: 'transparent transparent #dadada transparent',
                };
            case 'left':
                return {
                    ...baseArrowStyles,
                    left: '100%',
                    top: `${clampedVerticalArrowPos}px`,
                    marginTop: '-0.6rem',
                    borderColor: 'transparent transparent transparent #dadada',
                };
            case 'right':
                return {
                    ...baseArrowStyles,
                    right: '100%',
                    top: `${clampedVerticalArrowPos}px`,
                    marginTop: '-0.6rem',
                    borderColor: 'transparent #dadada transparent transparent',
                };
            default:
                return {};
        }
    };

    const infoBoxContent = isVisible ? (
        <div
            ref={boxRef}
            id={tooltipId}
            className={`info-box-portal ${portalClassName} info-box-portal--${calculatedPosition}${
                isScrollable ? ' info-box-portal--scrollable' : ''
            }`.trim()}
            onMouseEnter={clearCloseTimeout}
            onMouseLeave={scheduleClose}
            style={{
                position: 'absolute',
                top: boxPosition.top,
                left: boxPosition.left,
                maxHeight: boxMaxHeight ? `${boxMaxHeight}px` : undefined,
            }}
            role="tooltip"
        >
            {children}
            <div style={getArrowStyles()} />
        </div>
    ) : null;

    return (
        <div
            ref={containerRef}
            className={`info-box-container ${className}`.trim()}
            onMouseEnter={handleMouseEnter}
            onMouseLeave={handleMouseLeave}
            onFocus={handleFocus}
            onBlur={handleBlur}
            onKeyDown={handleKeyDown}
            tabIndex={0}
            aria-label={triggerAriaLabel}
            aria-describedby={isVisible ? tooltipId : undefined}
        >
            <i
                ref={iconRef}
                className={`${iconClassName} info-box-icon`}
                aria-hidden="true"
            >
                help
            </i>
            <div
                aria-live="polite"
                aria-atomic="true"
                className="sr-only"
                style={{
                    position: 'absolute',
                    width: '1px',
                    height: '1px',
                    padding: 0,
                    margin: '-1px',
                    overflow: 'hidden',
                    clip: 'rect(0, 0, 0, 0)',
                    whiteSpace: 'nowrap',
                    border: 0,
                }}
            >
                {announceText}
            </div>
            {typeof document !== 'undefined' &&
                createPortal(
                    infoBoxContent,
                    windowContext?.document.body ?? document.body
                )}
        </div>
    );
};

export default InfoBox;
